import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const STEP_TITLE = "Základné nastavenie zákazníka";

const COUNTRY_OPTIONS = [
  { value: "sk", label: "Slovensko (SK)" },
  { value: "cz", label: "Česká republika (CZ)" },
];

/**
 * Step 1: Basic customer settings. All fields required.
 * Receives values, errors, and onChange from parent.
 */
const Step1BasicSettings = ({
  values,
  errors,
  onChange,
  onLookup,
  lookupBusy = false,
}) => {
  const [icoQuery, setIcoQuery] = React.useState("");

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    onChange(field, value);
  };

  const doLookup = () => {
    if (lookupBusy) return;
    const q = icoQuery.trim();
    if (q && typeof onLookup === "function") onLookup(q);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {STEP_TITLE}
        </Typography>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Prefill the form from the company register by IČO. */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  label="Vyhľadať podľa IČO"
                  value={icoQuery}
                  onChange={(e) => setIcoQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      doLookup();
                    }
                  }}
                  placeholder="napr. 31333532"
                  helperText="Predvyplní názov a adresu, IČO/DIČ/IČ DPH z registra"
                  sx={{ maxWidth: 280 }}
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={doLookup}
                  disabled={lookupBusy || !icoQuery.trim()}
                  startIcon={
                    lookupBusy ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{ height: 40, whiteSpace: "nowrap" }}
                >
                  Vyhľadať
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active !== false}
                    onChange={handleChange("active")}
                    color="primary"
                  />
                }
                label="Aktívny *"
              />
            </Grid>
            <Grid item xs={12} md={6} />

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Názov spoločnosti *"
                value={values.companyName ?? ""}
                onChange={handleChange("companyName")}
                error={!!errors.companyName}
                helperText={errors.companyName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Store Code *"
                value={values.storeCode ?? ""}
                onChange={handleChange("storeCode")}
                error={!!errors.storeCode}
                helperText={errors.storeCode}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ulica *"
                value={values.street ?? ""}
                onChange={handleChange("street")}
                error={!!errors.street}
                helperText={errors.street}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mesto *"
                value={values.city ?? ""}
                onChange={handleChange("city")}
                error={!!errors.city}
                helperText={errors.city}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PSČ *"
                value={values.zip ?? ""}
                onChange={handleChange("zip")}
                error={!!errors.zip}
                helperText={errors.zip}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.country}>
                <InputLabel>Krajina *</InputLabel>
                <Select
                  value={values.country ?? "sk"}
                  onChange={handleChange("country")}
                  label="Krajina *"
                >
                  {COUNTRY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meno *"
                value={values.firstName ?? ""}
                onChange={handleChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Priezvisko *"
                value={values.lastName ?? ""}
                onChange={handleChange("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={values.email ?? ""}
                onChange={handleChange("email")}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefón *"
                value={values.phone ?? ""}
                onChange={handleChange("phone")}
                error={!!errors.phone}
                helperText={errors.phone}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Step1BasicSettings;
